import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { A11yColorFilter, UserStore } from '@signal-store';

/**
 * Renders the colour-blind correction SVG filters (daltonisation — one
 * pre-folded feColorMatrix per deficiency) and applies the selected one
 * to <html> as an inline absolute-URL filter style.
 *
 * Applied to <html> (not <body>) so it composes with the contrast/saturation
 * filter on <body>. The absolute URL is required because <base href="/"> breaks
 * fragment-only `filter: url(#id)` references; the URL is re-built on every
 * navigation so it stays in sync with the current path.
 */
@Component({
  selector: 'cipo-a11y-color-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="cipo-cvd-protanopia" color-interpolation-filters="linearRGB">
          <feColorMatrix
            type="matrix"
            values="1.00000 0.00000 0.00000 0 0  0.25490 0.74510 0.00000 0 0  -0.30310 0.54510 0.75800 0 0  0 0 0 1 0"
          />
        </filter>
        <filter id="cipo-cvd-deuteranopia" color-interpolation-filters="linearRGB">
          <feColorMatrix
            type="matrix"
            values="1.30625 -0.30625 0.00000 0 0  0.00000 1.00000 0.00000 0 0  0.04375 0.25625 0.70000 0 0  0 0 0 1 0"
          />
        </filter>
        <filter id="cipo-cvd-tritanopia" color-interpolation-filters="linearRGB">
          <feColorMatrix
            type="matrix"
            values="0.95000 0.38250 -0.33250 0 0  0.00000 0.76550 0.23450 0 0  0.00000 0.00000 1.00000 0 0  0 0 0 1 0"
          />
        </filter>
      </defs>
    </svg>
  `,
  styles: [
    `
      :host {
        position: absolute;
        width: 0;
        height: 0;
        overflow: hidden;
      }
    `,
  ],
})
export class A11yColorFilterComponent {
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => this.apply(this.userStore.a11y().colorFilter));

    // Re-apply on navigation — the absolute url embeds the current path.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.apply(this.userStore.a11y().colorFilter));
  }

  private apply(colorFilter: A11yColorFilter) {
    const html = document.documentElement;
    if (colorFilter === 'normal') {
      html.style.removeProperty('filter');
      html.removeAttribute('data-a11y-color-filter');
      return;
    }
    const base = window.location.href.split('#')[0];
    html.style.setProperty('filter', `url("${base}#cipo-cvd-${colorFilter}")`);
    html.setAttribute('data-a11y-color-filter', colorFilter);
  }
}
